/**
 * Received Fax Component
 * 
 * Displays detailed view of a received fax with PDF viewer and AI summary.
 * Matches the provided design exactly with proper theme integration.
 * 
 * @component
 * @example
 * return <FaxDetails />
 */

import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    Box,
    Typography,
    IconButton,
    Button as MuiButton,
    Paper
} from "@mui/material";
import {
    ArrowBack as ArrowBackIcon,
    Download as DownloadIcon,
    ChevronLeft as ChevronLeftIcon,
    ChevronRight as ChevronRightIcon,
    Add as AddIcon,
    Remove as RemoveIcon
} from "@mui/icons-material";
import { useTheme } from "../../ThemeContext";
import CustomContainer from "../common/CustomContainer";
import FaxAgentService from "../../services/fax-agent-service";
import { message } from "antd";
import * as pdfjsLib from 'pdfjs-dist';

// Set up PDF.js worker
const setupPDFWorker = () => {
    // Use unpkg CDN which is more reliable and has the correct file structure
    // Using .mjs extension for ES modules (required for v5+)
    pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;
};

setupPDFWorker();

/**
 * Format fax number to +1 (XXX) XXX-XXXX format
 * @param {string} number - Raw fax number
 * @returns {string} Formatted fax number
 */
const formatFaxNumber = (number) => {
    if (!number) return 'N/A';
    
    // Remove all non-digits
    const digits = number.replace(/\D/g, '');
    
    // Handle different lengths
    if (digits.length === 11 && digits[0] === '1') {
        // Format as +1 (XXX) XXX-XXXX
        return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
    } else if (digits.length === 10) {
        // Assume US number without country code
        return `+1 (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    
    // Return original if format doesn't match
    return number;
};

const FaxDetails = () => {
    const navigate = useNavigate();
    const { faxId } = useParams();
    const { themeMode, currentTheme } = useTheme();

    // States for PDF viewer
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [zoomLevel, setZoomLevel] = useState(100);

    // API data states
    const [faxData, setFaxData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // PDF states - array to hold multiple PDF viewers
    const [pdfViewers, setPdfViewers] = useState([]); // Array of { pdfDoc, currentPage, numPages, loading, error, canvasRef }
    const [loadingAll, setLoadingAll] = useState(false);

    // Fetch fax details when component mounts
    useEffect(() => {
        const fetchFaxDetails = async () => {
            if (!faxId) {
                setError('No fax ID provided');
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                setError(null);

                const faxDetails = await FaxAgentService.getFaxDetails(faxId);

                // Update total pages from API data
                if (faxDetails.totalPages) {
                    setTotalPages(faxDetails.totalPages);
                } else if (faxDetails.files && faxDetails.files.length > 0) {
                    // Use pages from first file if totalPages not available
                    setTotalPages(faxDetails.files[0].pages || 1);
                }

                setFaxData(faxDetails);
            } catch (err) {
                console.error('❌ Error fetching fax details:', err);
                setError(err.message || 'Failed to load fax details');
                message.error('Failed to load fax details');
            } finally {
                setLoading(false);
            }
        };

        fetchFaxDetails();
    }, [faxId]);

    // Load all PDFs when faxData is available
    useEffect(() => {
        if (faxData && faxData.files && faxData.files.length > 0) {
            const loadAllPDFs = async () => {
                setLoadingAll(true);
                const viewers = [];

                // Helper function to format file type for display
                const formatFileType = (fileType) => {
                    if (!fileType) return 'Document';
                    // Convert camelCase to Title Case with spaces
                    const formatted = fileType
                        .replace(/([A-Z])/g, ' $1') // Add space before capital letters
                        .replace(/^./, str => str.toUpperCase()) // Capitalize first letter
                        .trim();
                    return formatted;
                };

                for (let i = 0; i < faxData.files.length; i++) {
                    const file = faxData.files[i];
                    viewers.push({
                        fileIndex: i,
                        fileName: formatFileType(file.fileType) || `Document ${i + 1}`,
                        fileType: file.fileType,
                        fileUrl: file.fileUrl,
                        pages: file.pages,
                        pdfDoc: null,
                        currentPage: 1,
                        numPages: 0,
                        zoomLevel: 100,
                        loading: true,
                        error: null,
                        canvasRef: React.createRef()
                    });
                }

                setPdfViewers(viewers);

                // Load each PDF using blob URLs to avoid CORS issues
                for (let i = 0; i < viewers.length; i++) {
                    try {
                        // Download file as blob via backend proxy to avoid CORS issues
                        console.log(`🔄 Downloading fax file ${i + 1}/${viewers.length} via proxy...`);
                        const blob = await FaxAgentService.downloadFaxFile(viewers[i].fileUrl);
                        
                        // Create blob URL for PDF.js
                        const blobUrl = URL.createObjectURL(blob);
                        viewers[i].blobUrl = blobUrl; // Store for cleanup
                        
                        // Load PDF from blob URL
                        const loadingTask = pdfjsLib.getDocument({
                            url: blobUrl,
                            cMapUrl: `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/cmaps/`,
                            cMapPacked: true,
                            withCredentials: false,
                            httpHeaders: {},
                            verbosity: pdfjsLib.VerbosityLevel.ERRORS
                        });

                        const pdf = await loadingTask.promise;

                        viewers[i].pdfDoc = pdf;
                        viewers[i].numPages = pdf.numPages;
                        viewers[i].loading = false;
                        
                        console.log(`✅ Successfully loaded PDF ${i + 1}: ${viewers[i].fileName} (${pdf.numPages} pages)`);

                    } catch (error) {
                        console.error(`❌ Error loading PDF ${i + 1}:`, error);
                        viewers[i].error = error.message;
                        viewers[i].loading = false;
                    }
                }

                setPdfViewers([...viewers]);
                setLoadingAll(false);
            };

            loadAllPDFs();
        }
        
        // Cleanup blob URLs on unmount or when faxData changes
        return () => {
            pdfViewers.forEach(viewer => {
                if (viewer.blobUrl) {
                    URL.revokeObjectURL(viewer.blobUrl);
                }
            });
        };
    }, [faxData]);

    // Render pages for each PDF viewer
    useEffect(() => {
        pdfViewers.forEach((viewer, index) => {
            if (viewer.pdfDoc && !viewer.loading && !viewer.error && viewer.canvasRef.current) {
                const renderPage = async () => {
                    try {
                        const page = await viewer.pdfDoc.getPage(viewer.currentPage);
                        const canvas = viewer.canvasRef.current;
                        if (!canvas) return;

                        const context = canvas.getContext('2d');
                        const viewport = page.getViewport({ scale: viewer.zoomLevel / 100 });
                        canvas.height = viewport.height;
                        canvas.width = viewport.width;

                        await page.render({
                            canvasContext: context,
                            viewport: viewport
                        }).promise;
                    } catch (error) {
                        console.error(`❌ Error rendering PDF ${index + 1}:`, error);
                    }
                };

                renderPage();
            }
        });
    }, [pdfViewers]);

    const handleBack = () => {
        navigate('/fax-agent');
    };

    const handleDownload = async () => {
        if (!faxData || !faxData.files || faxData.files.length === 0) {
            message.error('No PDF file available to download');
            return;
        }

        try {
            // Download file as blob via backend proxy to avoid CORS issues
            const fileUrl = faxData.files[0].fileUrl;
            const blob = await FaxAgentService.downloadFaxFile(fileUrl);
            
            // Create blob URL and trigger download
            const blobUrl = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = `fax_${faxData.faxId || faxData._id}_${new Date().toISOString().split('T')[0]}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
            // Clean up blob URL
            URL.revokeObjectURL(blobUrl);
            
            message.success('PDF download started');
        } catch (error) {
            console.error('❌ Error downloading PDF:', error);
            message.error('Failed to download PDF');
        }
    };


    // Handle page navigation for individual PDF
    const handlePrevPage = (viewerIndex) => {
        setPdfViewers(prev => {
            const updated = [...prev];
            if (updated[viewerIndex].currentPage > 1) {
                updated[viewerIndex].currentPage--;
            }
            return updated;
        });
    };

    const handleNextPage = (viewerIndex) => {
        setPdfViewers(prev => {
            const updated = [...prev];
            if (updated[viewerIndex].currentPage < updated[viewerIndex].numPages) {
                updated[viewerIndex].currentPage++;
            }
            return updated;
        });
    };

    // Handle zoom for individual PDF
    const handleZoomIn = (viewerIndex) => {
        setPdfViewers(prev => {
            const updated = [...prev];
            if (updated[viewerIndex].zoomLevel < 200) {
                updated[viewerIndex].zoomLevel += 10;
            }
            return updated;
        });
    };

    const handleZoomOut = (viewerIndex) => {
        setPdfViewers(prev => {
            const updated = [...prev];
            if (updated[viewerIndex].zoomLevel > 50) {
                updated[viewerIndex].zoomLevel -= 10;
            }
            return updated;
        });
    };

    // Loading state
    if (loading) {
        return (
            <CustomContainer>
                <Box sx={{
                    backgroundColor: themeMode === 'dark' ? '#1B1533' : '#f5f5f5',
                    minHeight: '100vh',
                    padding: '24px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}>
                    <img
                        style={{
                            width: '250px',
                            height: '250px'
                        }}
                        src={
                            themeMode === "dark"
                                ? "/icons/loader_white.gif"
                                : "/icons/loader_black.gif"
                        }
                        alt="Loading"
                    />
                </Box>
            </CustomContainer>
        );
    }

    // Error state
    if (error) {
        return (
            <CustomContainer>
                <Box sx={{
                    backgroundColor: themeMode === 'dark' ? '#1B1533' : '#f5f5f5',
                    minHeight: '100vh',
                    padding: '24px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: 2
                }}>
                    <Typography sx={{
                        color: themeMode === 'dark' ? '#ffffff' : '#000000',
                        fontSize: '18px'
                    }}>
                        Error: {error}
                    </Typography>
                    <MuiButton
                        variant="outlined"
                        onClick={handleBack}
                        sx={{
                            borderColor: themeMode === 'dark' ? '#ffffff' : '#000000',
                            color: themeMode === 'dark' ? '#ffffff' : '#000000',
                            '&:hover': {
                                borderColor: themeMode === 'dark' ? '#ffffff' : '#000000',
                                backgroundColor: themeMode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
                            }
                        }}
                    >
                        Back to Dashboard
                    </MuiButton>
                </Box>
            </CustomContainer>
        );
    }

    // No data state
    if (!faxData) {
        return (
            <CustomContainer>
                <Box sx={{
                    backgroundColor: themeMode === 'dark' ? '#1B1533' : '#f5f5f5',
                    minHeight: '100vh',
                    padding: '24px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                }}>
                    <Typography sx={{
                        color: themeMode === 'dark' ? '#ffffff' : '#000000',
                        fontSize: '18px'
                    }}>
                        No fax data found
                    </Typography>
                </Box>
            </CustomContainer>
        );
    }

    return (
        <Box sx={{
            minHeight: '100vh',
            maxWidth: '100%',
            padding: '24px',
            color: themeMode === 'dark' ? '#ffffff' : '#000000',
            boxSizing: 'border-box',
            overflow: 'hidden',
        }}>
            {/* Header */}
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '24px'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <IconButton
                        onClick={handleBack}
                        sx={{
                            color: themeMode === 'dark' ? '#ffffff' : '#000000',
                            '&:hover': {
                                backgroundColor: themeMode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
                            }
                        }}
                    >
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="h6" sx={{
                        fontWeight: 500,
                        color: themeMode === 'dark' ? '#ffffff' : '#000000'
                    }}>
                        {faxData?.subject || 'Fax Details'}
                    </Typography>
                    {faxData?.status && (
                        <Box sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            padding: '4px 12px',
                            backgroundColor: themeMode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                            borderRadius: '16px',
                            border: `1px solid ${themeMode === 'dark' ? '#302d4c' : '#e0e0e0'}`,
                            fontSize: '12px',
                            fontWeight: 500,
                            color: themeMode === 'dark' ? '#ffffff' : '#000000'
                        }}>
                            {faxData.status}
                        </Box>
                    )}
                </Box>

                {/* <MuiButton
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={handleDownload}
                    sx={{
                        borderColor: '#FF6B47',
                        color: '#FF6B47',
                        '&:hover': {
                            borderColor: '#FF6B47',
                            backgroundColor: 'rgba(255, 107, 71, 0.1)'
                        }
                    }}
                >
                    Download
                </MuiButton> */}
            </Box>

            {/* Main Content Grid */}
            <Box sx={{
                display: 'grid',
                gridTemplateColumns: '600px 1fr',
                gap: '32px',
                height: 'calc(100vh - 180px)',
                marginTop: '16px',
                maxWidth: '100%',
                overflow: 'hidden',
            }}>
                {/* Left Column - Fax Details */}
                <Paper sx={{
                    backgroundColor: 'transparent',
                    border: `1px solid ${themeMode === 'dark' ? '#302d4c' : '#e0e0e0'}`,
                    borderRadius: '12px',
                    padding: '24px',
                    color: themeMode === 'dark' ? '#ffffff' : '#000000',
                    boxShadow: themeMode === 'dark' ? 'none' : '0 2px 8px rgba(0,0,0,0.1)',
                    overflowY: 'auto',
                    height: '100%',
                    maxHeight: '100%',
                }}>
                    {/* From Section */}
                    <Box sx={{ marginBottom: '32px' }}>
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            marginBottom: '16px'
                        }}>
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="body2" sx={{
                                    color: themeMode === 'dark' ? '#9b99aa' : '#666666',
                                    marginBottom: '8px',
                                    fontSize: '14px'
                                }}>
                                    From
                                </Typography>
                                <Typography variant="h6" sx={{
                                    fontWeight: 600,
                                    color: themeMode === 'dark' ? '#ffffff' : '#000000',
                                    marginBottom: '4px',
                                    fontSize: '16px'
                                }}>
                                    {faxData.direction === 'OUTBOUND' ? (faxData.emailId || 'Unknown Sender') : (formatFaxNumber(faxData.fromNumber) || 'Unknown Sender')}
                                </Typography>
                                <Typography variant="body2" sx={{
                                    color: themeMode === 'dark' ? '#9b99aa' : '#666666',
                                    fontSize: '14px'
                                }}>
                                    Fax No : {formatFaxNumber(faxData.fromNumber)}
                                </Typography>
                            </Box>
                            <Box sx={{ textAlign: 'left', marginTop: '8px' }}>
                                <Box sx={{ display: 'flex', fontSize: '14px', color: themeMode === 'dark' ? '#ffffff' : '#000000', marginBottom: '2px' }}>
                                    <Typography variant="body2" sx={{ width: '60px' }}>Date :</Typography>
                                    <Typography variant="body2">
                                        {faxData.createdAt ? (() => {
                                            const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
                                            let isoStr = faxData.createdAt;
                                            if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?$/.test(faxData.createdAt)) {
                                                isoStr = faxData.createdAt + 'Z';
                                            }
                                            const date = new Date(isoStr);
                                            return date.toLocaleDateString('en-US', {
                                                year: 'numeric',
                                                month: 'short',
                                                day: 'numeric',
                                                timeZone: userTimeZone
                                            });
                                        })() : 'N/A'}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', fontSize: '14px', color: themeMode === 'dark' ? '#ffffff' : '#000000' }}>
                                    <Typography variant="body2" sx={{ width: '60px' }}>Time :</Typography>
                                    <Typography variant="body2">
                                        {faxData.createdAt ? (() => {
                                            const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
                                            let isoStr = faxData.createdAt;
                                            if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?$/.test(faxData.createdAt)) {
                                                isoStr = faxData.createdAt + 'Z';
                                            }
                                            const date = new Date(isoStr);
                                            return date.toLocaleTimeString('en-US', {
                                                hour: '2-digit',
                                                minute: '2-digit',
                                                hour12: true,
                                                timeZone: userTimeZone
                                            });
                                        })() : 'N/A'}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                        {/* Horizontal Rule */}
                        <Box sx={{
                            height: '1px',
                            backgroundColor: themeMode === 'dark' ? '#302d4c' : '#e0e0e0',
                            marginBottom: '24px'
                        }} />
                    </Box>

                    {/* To Section */}
                    <Box sx={{ marginBottom: '32px' }}>
                        <Box sx={{ marginBottom: '16px' }}>
                            <Typography variant="body2" sx={{
                                color: themeMode === 'dark' ? '#9b99aa' : '#666666',
                                marginBottom: '8px',
                                fontSize: '14px'
                            }}>
                                To
                            </Typography>
                            <Typography variant="h6" sx={{
                                fontWeight: 600,
                                color: themeMode === 'dark' ? '#ffffff' : '#000000',
                                marginBottom: '4px',
                                fontSize: '16px'
                            }}>
                                {faxData.direction === 'INBOUND' ? (faxData.emailId || 'Unknown Recipient') : (faxData.formattedToNumber || faxData.toNumber || 'Unknown Recipient')}
                            </Typography>
                            {faxData.direction === 'INBOUND' && (
                                <Typography variant="body2" sx={{
                                    color: themeMode === 'dark' ? '#9b99aa' : '#666666',
                                    fontSize: '14px'
                                }}>
                                    Fax No : {faxData.formattedToNumber || faxData.toNumber || 'N/A'}
                                </Typography>
                            )}
                        </Box>
                        {/* Horizontal Rule */}
                        <Box sx={{
                            height: '1px',
                            backgroundColor: themeMode === 'dark' ? '#302d4c' : '#e0e0e0',
                            marginBottom: '24px'
                        }} />
                    </Box>
                </Paper>

                {/* Center Column - Multiple PDF Viewers */}
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '24px',
                    overflow: 'auto',
                    height: '100%',
                }}>
                    {/* Loading State */}
                    {loadingAll && (
                        <Paper sx={{
                            backgroundColor: 'transparent',
                            border: `1px solid ${themeMode === 'dark' ? '#302d4c' : '#e0e0e0'}`,
                            borderRadius: '12px',
                            padding: '40px',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            minHeight: '600px',
                        }}>
                            <Box sx={{ textAlign: 'center' }}>
                                <img
                                    style={{ width: '150px', height: '150px' }}
                                    src={themeMode === "dark" ? "/icons/loader_white.gif" : "/icons/loader_black.gif"}
                                    alt="Loading"
                                />
                            </Box>
                        </Paper>
                    )}

                    {/* Render Each PDF Viewer */}
                    {!loadingAll && pdfViewers.map((viewer, viewerIndex) => (
                        <Paper 
                            key={`pdf-viewer-${viewerIndex}`}
                            sx={{
                                backgroundColor: 'transparent',
                                border: `1px solid ${themeMode === 'dark' ? '#302d4c' : '#e0e0e0'}`,
                                borderRadius: '12px',
                                padding: '16px',
                                display: 'flex',
                                flexDirection: 'column',
                                boxShadow: themeMode === 'dark' ? 'none' : '0 2px 8px rgba(0,0,0,0.1)',
                            }}
                        >
                            {/* PDF Name Header */}
                            <Typography
                                sx={{
                                    fontSize: "16px",
                                    fontWeight: 600,
                                    color: themeMode === 'dark' ? "#ffffff" : "#000000",
                                    marginBottom: "12px",
                                    padding: "8px 12px",
                                    backgroundColor: themeMode === 'dark' ? "#252144" : "#f5f5f5",
                                    borderRadius: "8px",
                                    border: `1px solid ${themeMode === 'dark' ? "#302d4c" : "#e0e0e0"}`,
                                }}
                            >
                                📄 {viewer.fileName}
                            </Typography>

                            {/* PDF Controls */}
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: '12px',
                                padding: '8px 12px',
                                backgroundColor: themeMode === 'dark' ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.05)',
                                borderRadius: '8px'
                            }}>
                                {/* Page Navigation */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <IconButton
                                        size="small"
                                        onClick={() => handlePrevPage(viewerIndex)}
                                        disabled={viewer.currentPage === 1}
                                        sx={{
                                            color: themeMode === 'dark' ? '#ffffff' : '#000000',
                                            '&:disabled': {
                                                color: themeMode === 'dark' ? '#9b99aa' : '#cccccc'
                                            }
                                        }}
                                    >
                                        <ChevronLeftIcon />
                                    </IconButton>
                                    <Typography variant="body2" sx={{
                                        color: themeMode === 'dark' ? '#ffffff' : '#000000',
                                        minWidth: '60px',
                                        textAlign: 'center',
                                        fontSize: '13px'
                                    }}>
                                        {viewer.currentPage}/{viewer.numPages}
                                    </Typography>
                                    <IconButton
                                        size="small"
                                        onClick={() => handleNextPage(viewerIndex)}
                                        disabled={viewer.currentPage === viewer.numPages}
                                        sx={{
                                            color: themeMode === 'dark' ? '#ffffff' : '#000000',
                                            '&:disabled': {
                                                color: themeMode === 'dark' ? '#9b99aa' : '#cccccc'
                                            }
                                        }}
                                    >
                                        <ChevronRightIcon />
                                    </IconButton>
                                </Box>

                                {/* Zoom Controls */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <IconButton
                                        size="small"
                                        onClick={() => handleZoomOut(viewerIndex)}
                                        disabled={viewer.zoomLevel <= 50}
                                        sx={{
                                            color: themeMode === 'dark' ? '#ffffff' : '#000000',
                                            '&:disabled': {
                                                color: themeMode === 'dark' ? '#9b99aa' : '#cccccc'
                                            }
                                        }}
                                    >
                                        <RemoveIcon />
                                    </IconButton>
                                    <Typography variant="body2" sx={{
                                        color: themeMode === 'dark' ? '#ffffff' : '#000000',
                                        minWidth: '50px',
                                        textAlign: 'center'
                                    }}>
                                        {viewer.zoomLevel}%
                                    </Typography>
                                    <IconButton
                                        size="small"
                                        onClick={() => handleZoomIn(viewerIndex)}
                                        disabled={viewer.zoomLevel >= 200}
                                        sx={{
                                            color: themeMode === 'dark' ? '#ffffff' : '#000000',
                                            '&:disabled': {
                                                color: themeMode === 'dark' ? '#9b99aa' : '#cccccc'
                                            }
                                        }}
                                    >
                                        <AddIcon />
                                    </IconButton>
                                </Box>
                            </Box>

                            {/* PDF Canvas */}
                            <Box sx={{
                                flex: 1,
                                backgroundColor: themeMode === 'dark' ? '#1B1A33' : '#ffffff',
                                borderRadius: '8px',
                                padding: '20px',
                                overflow: 'auto',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                minHeight: '500px',
                            }}>
                                {viewer.loading && (
                                    <Box sx={{ textAlign: 'center' }}>
                                        <img
                                            style={{ width: '100px', height: '100px' }}
                                            src={themeMode === "dark" ? "/icons/loader_white.gif" : "/icons/loader_black.gif"}
                                            alt="Loading"
                                        />
                                    </Box>
                                )}

                                {viewer.error && (
                                    <Typography sx={{ color: themeMode === 'dark' ? '#ff6b6b' : '#d32f2f' }}>
                                        Error: {viewer.error}
                                    </Typography>
                                )}

                                {!viewer.loading && !viewer.error && viewer.pdfDoc && (
                                    <canvas
                                        ref={viewer.canvasRef}
                                        style={{
                                            border: `1px solid ${themeMode === 'dark' ? '#302d4c' : '#ccc'}`,
                                            borderRadius: '4px',
                                            backgroundColor: 'white',
                                            boxShadow: themeMode === 'dark' ? '0 2px 8px rgba(0,0,0,0.5)' : '0 2px 8px rgba(0,0,0,0.1)',
                                            maxWidth: '100%',
                                            height: 'auto',
                                        }}
                                    />
                                )}
                            </Box>
                        </Paper>
                    ))}

                    {/* No PDFs State */}
                    {!loadingAll && pdfViewers.length === 0 && (
                        <Paper sx={{
                            backgroundColor: 'transparent',
                            border: `1px solid ${themeMode === 'dark' ? '#302d4c' : '#e0e0e0'}`,
                            borderRadius: '12px',
                            padding: '40px',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            minHeight: '600px',
                        }}>
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="h6" sx={{
                                    color: themeMode === 'dark' ? '#ffffff' : '#666666',
                                    marginBottom: '8px'
                                }}>
                                    No PDF Available
                                </Typography>
                                <Typography variant="body2" sx={{
                                    color: themeMode === 'dark' ? '#9b99aa' : '#999999',
                                }}>
                                    This fax does not contain any PDF files to display
                                </Typography>
                            </Box>
                        </Paper>
                    )}
                </Box>

                {/* Right Column - AI Summary */}
                {/* 
                <Paper sx={{
                    backgroundColor: currentTheme?.callhistory?.cardBackground || '#2A2654',
                    border: `1px solid ${currentTheme?.border?.primary || '#302d4c'}`,
                    borderRadius: '12px',
                    padding: '20px',
                    color: currentTheme?.text?.primary || '#ffffff'
                }}>
                    <Typography variant="h6" sx={{ 
                        fontWeight: 500,
                        marginBottom: '20px',
                        color: currentTheme?.text?.primary || '#ffffff'
                    }}>
                        AI Fax Summary
                    </Typography>

                    {/* Patient Details */}
                {/* <Box sx={{ marginBottom: '20px' }}>
                        <Typography variant="body2" sx={{ 
                            color: currentTheme?.text?.secondary || '#9b99aa',
                            marginBottom: '12px'
                        }}>
                            Patient Details
                        </Typography>
                        <Typography variant="body1" sx={{ 
                            fontWeight: 500,
                            color: currentTheme?.text?.primary || '#ffffff',
                            marginBottom: '4px'
                        }}>
                            Name : {faxData.aiSummary.patientName}
                        </Typography>
                        <Typography variant="body2" sx={{ 
                            color: currentTheme?.text?.secondary || '#9b99aa'
                        }}>
                            ID : {faxData.aiSummary.patientId}
                        </Typography>
                    </Box>

                    {/* Summary Text */}
                {/* <Box>
                        <Typography variant="body2" sx={{ 
                            color: currentTheme?.text?.primary || '#ffffff',
                            lineHeight: 1.6,
                            fontSize: '14px',
                            marginBottom: '16px'
                        }}>
                            {faxData.aiSummary.summaryText}
                        </Typography>
                        
                        <Typography variant="body2" sx={{ 
                            color: currentTheme?.text?.primary || '#ffffff',
                            lineHeight: 1.6,
                            fontSize: '14px',
                            marginBottom: '16px'
                        }}>
                            {faxData.aiSummary.additionalDetails[0]}
                        </Typography>

                        {/* Bullet Points */}
                {/* <Box component="ul" sx={{ 
                            paddingLeft: '20px',
                            margin: 0,
                            '& li': {
                                marginBottom: '8px',
                                color: currentTheme?.text?.primary || '#ffffff',
                                fontSize: '14px',
                                lineHeight: 1.5
                            }
                        }}>
                            <li>{faxData.aiSummary.additionalDetails[1]}</li>
                            <li>{faxData.aiSummary.additionalDetails[2]}</li>
                        </Box>
                    </Box>
                </Paper> */}
            </Box>
        </Box>
    );
};

export default FaxDetails;
